import moment from 'moment';
import { FC, useCallback } from 'react';
import RdSingleDatePicker from 'react-dates/lib/components/SingleDatePicker';
import SingleDatePickerShape from 'react-dates/lib/shapes/SingleDatePickerShape';
import registerCSSInterfaceWithDefaultTheme from 'react-dates/lib/utils/registerCSSInterfaceWithDefaultTheme';
import { FormItem, IFormItemProps } from '../Form/FormItem';
import { Icon } from '../Icon/Icon';
import { IDatePickerProps, isOutsideRangeCheck } from './DateRangePicker';
import { StyledDatePicker } from './styles';

registerCSSInterfaceWithDefaultTheme();

type ISingleDatePickerProps = IDatePickerProps &
  SingleDatePickerShape &
  IFormItemProps & {
    /** The minimum allowed date. All dates before this will be disabled. This will override `isPastDatesOnly` and `isFutureDatesOnly`. */
    minDate?: moment.Moment;
    /** The maximum allowed date. All dates after this will be disabled. This will override `isPastDatesOnly` and `isFutureDatesOnly`. */
    maxDate?: moment.Moment;
  };

export const SingleDatePicker: FC<ISingleDatePickerProps> = ({
  dateLimit = 'year',
  dateLimitValue = 1,
  disabled,
  hint,
  isFutureDatesOnly = false,
  isMini = false,
  isPastDatesOnly = false,
  label,
  spacingBelow,
  tooltipDescription,
  tooltipHeading,
  tooltipWidth,
  validationMessage,
  validationState,
  width,
  minDate,
  maxDate,
  ...singleDatePickerProps
}) => {
  const effectiveDisplayFormat = (singleDatePickerProps as any)?.displayFormat ?? 'DD/MM/YYYY';
  const effectiveDayAriaLabelFormat = (singleDatePickerProps as any)?.dayAriaLabelFormat ?? 'dddd, D MMMM YYYY';

  const isOutsideRange = useCallback(
    (day: moment.Moment) => isOutsideRangeCheck(day, dateLimitValue, dateLimit, isPastDatesOnly, isFutureDatesOnly),
    [dateLimitValue, dateLimit, isPastDatesOnly, isFutureDatesOnly],
  );

  const isOutsideRangeUsingMinMaxDates = useCallback(
    (day: moment.Moment) => {
      if (!minDate && !maxDate) {
        return false;
      }

      if (minDate && !maxDate) {
        return day.isBefore(minDate, 'date');
      }

      if (!minDate && maxDate) {
        return day.isAfter(maxDate, 'date');
      }

      return !day.isBetween(minDate, maxDate, 'date', '[]');
    },
    [maxDate, minDate],
  );

  return (
    <StyledDatePicker isMini={isMini} width={width}>
      <FormItem
        disabled={disabled}
        for="date"
        hint={hint}
        isMini={isMini}
        label={label}
        spacingBelow={spacingBelow}
        tooltipDescription={tooltipDescription}
        tooltipHeading={tooltipHeading}
        tooltipWidth={tooltipWidth}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        <RdSingleDatePicker
          customInputIcon={isMini ? <Icon icon="angle-down" /> : null}
          customCloseIcon={<Icon icon="times" />}
          disabled={disabled}
          inputIconPosition="after"
          isOutsideRange={minDate || maxDate ? isOutsideRangeUsingMinMaxDates : isOutsideRange}
          readOnly={true}
          showClearDate={!isMini}
          small={isMini}
          {...singleDatePickerProps}
          displayFormat={effectiveDisplayFormat}
          dayAriaLabelFormat={effectiveDayAriaLabelFormat}
        />
      </FormItem>
    </StyledDatePicker>
  );
};
